import React from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'
import { Address, Country } from '../../swagger'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { CircularProgress } from '@mui/material'
import useStatesForCountry from '../../hooks/useStatesForCountry'
import { LoadingContext } from '../Context/LoadingContext'

const AddressForm: React.FC<{
  addressInformation: Address
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  countryOptions: Country[]
  billingAddress?: boolean
  isCardSelected?: boolean
}> = ({
  addressInformation,
  onInputChange,
  onInputBlur,
  countryOptions,
  billingAddress = false,
  isCardSelected = false,
}) => {
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()

  const countryOptionsDefault =
    countryOptions.length > 0 && !!addressInformation.countryCode
      ? addressInformation.countryCode
      : 'US'
  const { addLoadingIds } = React.useContext(LoadingContext)

  const isStateDisabled =
    addressInformation.countryCode === '' && countryOptionsDefault === ''

  const { defaultStates: defaultStateOptions, loadingId } = useStatesForCountry(
    {
      countryCode: countryOptionsDefault,
    }
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /** Whenever the country changes, update the states */
    if (event.target.name === 'countryCode') {
      addLoadingIds([loadingId])
    }

    onInputChange(event)
  }

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    /** Whenever the country changes, update the states */
    if (event.target.name === 'countryCode') {
      addLoadingIds([loadingId])
    }

    onInputBlur(event)
  }

  if (isCardSelected && countryOptions.length === 0) {
    return <CircularProgress />
  }

  const addressFields = {
    streetAddress1: (
      <TextField
        id="streetAddress1"
        name="streetAddress1"
        variant="filled"
        label={t('Address.FormFields.StreetAddress1', 'Street Address')}
        value={addressInformation.streetAddress1}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      />
    ),

    streetAddress2: (
      <TextField
        id="streetAddress2"
        name="streetAddress2"
        variant="filled"
        label={t('Address.FormFields.StreetAddress2', 'Building, Suite, etc.')}
        value={addressInformation.streetAddress2}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      />
    ),
    city: (
      <TextField
        id="city"
        name="city"
        variant="filled"
        label={t('Address.FormFields.City', 'City')}
        value={addressInformation.city}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      />
    ),
    country: (
      <TextField
        id="countryCode"
        name="countryCode"
        variant="filled"
        select
        label={t('Address.FormFields.Country', 'Country')}
        value={countryOptions.length > 0 ? addressInformation.countryCode : ''}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      >
        {countryOptions.map((country) => (
          <MenuItem key={country.name} value={country.countryCode}>
            {country.name}
          </MenuItem>
        ))}
      </TextField>
    ),
    state: (
      <TextField
        id="state"
        name="state"
        variant="filled"
        select={!isStateDisabled}
        disabled={isStateDisabled}
        label={t('Address.FormFields.State', 'State/Province')}
        value={defaultStateOptions.length > 0 ? addressInformation.state : ''}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      >
        {defaultStateOptions.map((state) => (
          <MenuItem key={state.name} value={state.stateCode}>
            {state.name}
          </MenuItem>
        ))}
      </TextField>
    ),
    zip: (
      <TextField
        id="zip"
        name="zip"
        variant="filled"
        label={t('Address.FormFields.ZipCode', 'Postal Code')}
        value={addressInformation.zip}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        fullWidth={billingAddress}
      />
    ),
  }

  return (
    <>
      {!!billingAddress ? (
        <Box display="flex" flexDirection="column">
          <Box mb={3}>{addressFields.streetAddress1}</Box>
          <Box mb={3}>{addressFields.streetAddress2}</Box>
          {!!isCardSelected && <Box mb={3}>{addressFields.country}</Box>}
          <Box mb={3}>{addressFields.city}</Box>
          <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }}>
            <Box
              width={{ sm: '100%', lg: '50%' }}
              pr={{ sm: 0, lg: 2 }}
              mb={{ xs: 3, lg: 0 }}
            >
              {addressFields.state}
            </Box>
            <Box
              width={{ sm: '100%', lg: '50%' }}
              pl={{ sm: 0, lg: 2 }}
              mb={{ xs: 3, lg: 0 }}
            >
              {addressFields.zip}
            </Box>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={showOnDesktop ? 3 : 0}>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" pb={3}>
              {addressFields.streetAddress1}
              {addressFields.streetAddress2}
              {addressFields.city}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column">
              {addressFields.country}
              {addressFields.state}
              {addressFields.zip}
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default AddressForm
